/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { isEqual } from 'lodash';
import { LayerType } from '@opengeoweb/webmap';
import type { FeatureLayerData } from './types';
import { layerActions } from '../../map/layer/reducer';
import { LayerActionOrigin } from '../../map/layer/types';
import type {
  FeatureSyncLayerMetadata,
  LayerState,
  ReduxLayer,
} from '../../map/layer/types';

export const makeFeatureSyncLayerId = (
  _panelId: string,
  featureLayerKey: string,
  mapId: string,
): string => `${featureLayerKey}_${mapId}`;

export const getFeatureSyncLayerMetadata = (
  layer?: ReduxLayer,
): FeatureSyncLayerMetadata | undefined => layer?.featureSync;

export const getFeatureSyncLayersForPanel = (
  layersState: LayerState | undefined,
  panelId: string,
): { layerId: string; featureLayerKey: string; mapId: string }[] => {
  if (!layersState) {
    return [];
  }

  return layersState.allIds
    .map((layerId) => {
      const layer = layersState.byId[layerId];
      const metadata = getFeatureSyncLayerMetadata(layer);
      return {
        layerId,
        sourcePanelId: metadata?.sourcePanelId ?? '',
        featureLayerKey: metadata?.featureLayerKey ?? layer?.name ?? '',
        mapId: layer?.mapId ?? '',
      };
    })
    .filter(
      ({ sourcePanelId, featureLayerKey, mapId }) =>
        sourcePanelId === panelId && !!featureLayerKey && !!mapId,
    )
    .map(({ layerId, featureLayerKey, mapId }) => ({
      layerId,
      featureLayerKey,
      mapId,
    }));
};

export const upsertFeatureSyncLayer = (
  layersState: LayerState | undefined,
  dispatch: (action: unknown) => void,
  panelId: string,
  featureLayerKey: string,
  mapId: string,
  featureLayerData: FeatureLayerData,
): void => {
  const layerId = makeFeatureSyncLayerId(panelId, featureLayerKey, mapId);
  const existingLayer = layersState?.byId[layerId];

  if (existingLayer) {
    if (isEqual(existingLayer.geojson, featureLayerData.geoJSON)) {
      return;
    }

    dispatch(
      layerActions.layerChangeGeojson({
        layerId,
        geojson: featureLayerData.geoJSON,
      }),
    );

    return;
  }

  dispatch(
    layerActions.addLayer({
      layerId,
      mapId,
      layer: {
        id: layerId,
        layerType: LayerType.featureLayer,
        geojson: featureLayerData.geoJSON,
        originalId: featureLayerData.originalId,
        name: featureLayerKey,
        mapId,
        featureSync: {
          sourcePanelId: panelId,
          featureLayerKey,
        },
      },
      origin: LayerActionOrigin.featureSync,
    }),
  );
};
